import React, { ReactElement, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

import {
  CollectiveDirectoryPillLabel,
  CollectiveDirectoryPillWrapper,
} from 'app/components/CommonStyled/CollectiveDirectoryPill/styled';
import {
  ClientIcon,
  PassionIcon,
  SkillIcon,
} from 'app/components/DetailsPages/shared/CommonGround/Icons';
import usePeopleFilters from 'app/components/People/hooks/usePeopleFilters';
import { DisciplineIcon, SearchIconSmall } from 'app/components/shared/icons';
import useBehavioral, { BEHAVIORALS } from 'hooks/useBehavioral';
import { setAboutKyuOsOpen } from 'store/reducers/onboarding-reducer';
import {
  IPeopleFilter,
  IPeopleFilters,
  PEOPLE_FILTER_TYPE,
} from 'store/reducers/people/people-reducer';
import { arrayToOxford } from 'utils';

import useKyu101 from '../../hooks/useKyu101';
import {
  AboutKyuOsNetworkIntroSectionContainer,
  AboutKyuOsNetworkIntroSectionDescription,
  AboutKyuOsNetworkIntroSectionInformationContainer,
  AboutKyuOsNetworkIntroSectionPillsContainer,
  AboutKyuOsNetworkIntroSectionsWrapper,
  AboutKyuOsNetworkIntroSectionTitle,
  AboutKyuOsNetworkIntroTitle,
  AboutKyuOsNetworkIntroWrapper,
} from './styled';

const correctTypesMap = new Map<string, string>([['cities', 'locations']]);

interface AboutKyuOsNetworkPromptPillProps {
  children: React.ReactNode;
  label: string;
  prompt: string;
  onClick?: () => void;
}

interface AboutKyuOsNetworkIntroFilters {
  disciplines?: IPeopleFilter[];
  passions?: IPeopleFilter[];
  skills?: IPeopleFilter[];
  clients?: IPeopleFilter[];
  city?: IPeopleFilter[];
}

function AboutKyuOsNetworkPromptPill({
  children,
  label,
  prompt,
  onClick,
}: Readonly<AboutKyuOsNetworkPromptPillProps>) {
  const { push } = useHistory();

  const handleClick = () => {
    onClick?.();
    push(`/people?prompt=${encodeURIComponent(prompt)}`);
  };

  return (
    <CollectiveDirectoryPillWrapper onClick={handleClick}>
      {React.cloneElement(children as ReactElement, {
        width: 16,
        height: 16,
        fill: 'var(--color-primary)',
      })}
      <CollectiveDirectoryPillLabel>{label}</CollectiveDirectoryPillLabel>
    </CollectiveDirectoryPillWrapper>
  );
}

export default function AboutKyuOsNetworkIntro() {
  const { data: peopleFiltersData } = usePeopleFilters<AboutKyuOsNetworkIntroFilters>();
  const data = peopleFiltersData as AboutKyuOsNetworkIntroFilters | undefined;
  const { data: pillsData } = useKyu101();
  const dispatch = useDispatch();

  const casualIntentionCollectiveDirectoryOpenedBehavioralFunction = useBehavioral(
    BEHAVIORALS.KYU101_CASUAL_INTENTION_COLLECTIVE_DIRECTORY_OPENED
  );
  const expertiseIntentionCollectiveDirectoryOpenedBehavioralFunction = useBehavioral(
    BEHAVIORALS.KYU101_EXPERTISE_INTENTION_COLLECTIVE_DIRECTORY_OPENED
  );
  const conveneIntentionCollectiveDirectoryOpenedBehavioralFunction = useBehavioral(
    BEHAVIORALS.KYU101_CONVENE_INTENTION_COLLECTIVE_DIRECTORY_OPENED
  );

  const filtersToDetails = (filters: IPeopleFilters) => {
    return Object.keys(filters)
      .reduce((acc, key) => {
        if (filters[key].length) {
          acc.push({
            title: key,
            values: filters[key],
          });
        }

        return acc;
      }, [])
      .map((filter) => `${filter.title}: ${arrayToOxford(filter.values.map((v) => v.name))}`)
      .join(' | ');
  };

  const handlePillBeforeClick = () => {
    dispatch(setAboutKyuOsOpen(false));
  };

  const handleCasualIntentionBeforeClick = (filters: IPeopleFilters) => {
    handlePillBeforeClick();
    casualIntentionCollectiveDirectoryOpenedBehavioralFunction({
      action_details: filtersToDetails(filters),
    });
  };

  const handleExpertiseIntentionBeforeClick = (filters: IPeopleFilters) => {
    handlePillBeforeClick();
    expertiseIntentionCollectiveDirectoryOpenedBehavioralFunction({
      action_details: filtersToDetails(filters),
    });
  };

  const handleConveneIntentionBeforeClick = (filters: IPeopleFilters) => {
    handlePillBeforeClick();
    conveneIntentionCollectiveDirectoryOpenedBehavioralFunction({
      action_details: filtersToDetails(filters),
    });
  };

  const getPromptFromFilters = (label: string, filters: IPeopleFilter[]) => {
    return `${label}: ${arrayToOxford(filters.map((filter) => filter.name))}`;
  };

  const getPillFilters = (pill) => {
    return pill.items.reduce((acc, item) => {
      acc[correctTypesMap.has(item.type) ? correctTypesMap.get(item.type) : item.type] = [
        ...(acc[correctTypesMap.has(item.type) ? correctTypesMap.get(item.type) : item.type] || []),
        item,
      ];
      return acc;
    }, {});
  };

  const beIntentionalPills = useMemo(() => {
    if (!pillsData) return [];

    return pillsData?.kyu_101.filter((pill) => pill.section === 'be_intentional');
  }, [pillsData]);

  const convenePeoplePills = useMemo(() => {
    if (!pillsData) return [];

    return pillsData?.kyu_101.filter((pill) => pill.section === 'convene_people');
  }, [pillsData]);

  return (
    <AboutKyuOsNetworkIntroWrapper>
      <AboutKyuOsNetworkIntroTitle>
        The Collective is full of diverse people from all over the globe. Connecting to folks at
        other companies can sometimes feel daunting, so here are our top three tips for getting
        started:
      </AboutKyuOsNetworkIntroTitle>
      <AboutKyuOsNetworkIntroSectionsWrapper>
        <AboutKyuOsNetworkIntroSectionContainer>
          <img src="/images/about-kyu-os/sections/coffee-chat.svg" />
          <AboutKyuOsNetworkIntroSectionInformationContainer>
            <AboutKyuOsNetworkIntroSectionTitle>
              Start with a coffee chat
            </AboutKyuOsNetworkIntroSectionTitle>
            <AboutKyuOsNetworkIntroSectionDescription>
              Looking for advice or a new perspective on a work challenge? Consider reaching out for
              a low-stakes chat. Try it out:
            </AboutKyuOsNetworkIntroSectionDescription>
            <AboutKyuOsNetworkIntroSectionPillsContainer>
              {data?.disciplines?.length > 0 ? (
                <AboutKyuOsNetworkPromptPill
                  label="People in my discipline"
                  prompt={getPromptFromFilters('People in my discipline', data.disciplines)}
                  onClick={() =>
                    handleCasualIntentionBeforeClick({
                      [PEOPLE_FILTER_TYPE.DISCIPLINES]: data.disciplines,
                    })
                  }
                >
                  <DisciplineIcon />
                </AboutKyuOsNetworkPromptPill>
              ) : null}
              {data?.passions?.length > 0 ? (
                <AboutKyuOsNetworkPromptPill
                  label="People who share my passions"
                  prompt={getPromptFromFilters('People who share my passions', data.passions)}
                  onClick={() =>
                    handleCasualIntentionBeforeClick({
                      [PEOPLE_FILTER_TYPE.PASSIONS]: data.passions,
                    })
                  }
                >
                  <PassionIcon />
                </AboutKyuOsNetworkPromptPill>
              ) : null}
              {data?.skills?.length > 0 ? (
                <AboutKyuOsNetworkPromptPill
                  label="People who share my skills"
                  prompt={getPromptFromFilters('People who share my skills', data.skills)}
                  onClick={() =>
                    handleCasualIntentionBeforeClick({
                      [PEOPLE_FILTER_TYPE.SKILLS]: data.skills,
                    })
                  }
                >
                  <SkillIcon />
                </AboutKyuOsNetworkPromptPill>
              ) : null}
              {data?.clients?.length > 0 ? (
                <AboutKyuOsNetworkPromptPill
                  label="People who have worked with the same clients "
                  prompt={getPromptFromFilters(
                    'People who have worked with the same clients',
                    data.clients
                  )}
                  onClick={() =>
                    handleCasualIntentionBeforeClick({
                      [PEOPLE_FILTER_TYPE.CLIENTS]: data.clients,
                    })
                  }
                >
                  <ClientIcon />
                </AboutKyuOsNetworkPromptPill>
              ) : null}
              {data?.city?.length > 0 ? (
                <AboutKyuOsNetworkPromptPill
                  label="People in my city"
                  prompt={getPromptFromFilters('People in my city', data.city)}
                  onClick={() =>
                    handleCasualIntentionBeforeClick({
                      [PEOPLE_FILTER_TYPE.LOCATIONS]: data.city,
                    })
                  }
                >
                  <ClientIcon />
                </AboutKyuOsNetworkPromptPill>
              ) : null}
            </AboutKyuOsNetworkIntroSectionPillsContainer>
          </AboutKyuOsNetworkIntroSectionInformationContainer>
        </AboutKyuOsNetworkIntroSectionContainer>
        <AboutKyuOsNetworkIntroSectionContainer>
          <img src="/images/about-kyu-os/sections/intentional.svg" />
          <AboutKyuOsNetworkIntroSectionInformationContainer>
            <AboutKyuOsNetworkIntroSectionTitle>Be intentional</AboutKyuOsNetworkIntroSectionTitle>
            <AboutKyuOsNetworkIntroSectionDescription>
              Slack is super useful, but finding the right channel can be a challenge. Put in your
              search criteria into the Collective Directory to target you ask to the right people.
              Try out one of the filters below:
            </AboutKyuOsNetworkIntroSectionDescription>
            <AboutKyuOsNetworkIntroSectionPillsContainer>
              {beIntentionalPills.map((pill) => (
                <AboutKyuOsNetworkPromptPill
                  key={pill.name}
                  label={pill.name}
                  prompt={pill.name}
                  onClick={() => handleExpertiseIntentionBeforeClick(getPillFilters(pill))}
                >
                  <SearchIconSmall />
                </AboutKyuOsNetworkPromptPill>
              ))}
            </AboutKyuOsNetworkIntroSectionPillsContainer>
          </AboutKyuOsNetworkIntroSectionInformationContainer>
        </AboutKyuOsNetworkIntroSectionContainer>
        <AboutKyuOsNetworkIntroSectionContainer>
          <img src="/images/about-kyu-os/sections/convene-people.svg" />
          <AboutKyuOsNetworkIntroSectionInformationContainer>
            <AboutKyuOsNetworkIntroSectionTitle>Convene people</AboutKyuOsNetworkIntroSectionTitle>
            <AboutKyuOsNetworkIntroSectionDescription>
              You don’t need to wait for kyu. Find out who’s doing similar work as you and convene
              them. Take the #collective-ai group for example - it’s a fully self-organized group of
              AI enthusiasts from al the companies. Try out one of the filters below:
            </AboutKyuOsNetworkIntroSectionDescription>
            <AboutKyuOsNetworkIntroSectionPillsContainer>
              {convenePeoplePills.map((pill) => (
                <AboutKyuOsNetworkPromptPill
                  key={pill.name}
                  label={pill.name}
                  prompt={pill.name}
                  onClick={() => handleConveneIntentionBeforeClick(getPillFilters(pill))}
                >
                  <SearchIconSmall />
                </AboutKyuOsNetworkPromptPill>
              ))}
            </AboutKyuOsNetworkIntroSectionPillsContainer>
          </AboutKyuOsNetworkIntroSectionInformationContainer>
        </AboutKyuOsNetworkIntroSectionContainer>
      </AboutKyuOsNetworkIntroSectionsWrapper>
    </AboutKyuOsNetworkIntroWrapper>
  );
}
